import { motion } from "framer-motion";
import { Calendar, CheckCircle } from "lucide-react";

export const ScreenFreeCourse = () => {
  const course1Quarters = [
    {
      title: "Quarter 1",
      description: [
        "Develop sorting and patterning skills in a sequential order, encouraging logical decision-making through understanding relationships, attributes, and their applications to sets.",
        "Introduce basic coding concepts such as sequencing and loops, along with their real-world applications.",
        "Exercise algorithmic thinking to solve problems or perform tasks systematically and efficiently.",
      ],
      image: "/images/boy-playing-with-colorful-atoms-game-home.jpg",
    },
    {
      title: "Quarter 2",
      description: [
        "Introduction to Binary Code, the fundamental language underlying all computer programming.",
        "Explore the mysteries of images and pixels, understanding how digital visuals are formed and represented in computers.",
        "Understand computer networks and explore challenges such as deadlocks and contention, applying computational thinking to analyze and solve them.",
      ],
      image: "/images/close-up-hand-playing-game-table.jpg",
    },
    {
      title: "Quarter 3",
      description: [
        "Understand fractions and their role in problem-solving and real-world applications.",
        "Introduction to probability and its modern uses in data analysis, decision-making, and everyday situations.",
        "Explore the fascinating world of encryption and ciphers, discovering how information is secured in the digital age.",
        "Program bots optimally by applying computational and analytical skills developed in previous quarters.",
      ],
      image: "/images/child-girl-sits-white-table-solving-arithmetic-problem.jpg",
    },
    {
      title: "Quarter 4",
      description: [
        "Explore the fascinating world of circuits, learning how electricity flows through hands-on activities and visual experiments.",
        "Discover logic gates to understand how computers make decisions and how truth tables represent these logical operations.",
        "Apply geometric transformations and angles, connecting abstract math concepts to real-world designs and technologies.",
      ],
      image: "/images/little-boy-kid-child-constructor-checking-technical-toy.jpg",
    },
  ];

  return (
    <div className="w-full bg-gray-50 min-h-screen">
      <div className="bg-primary text-white py-20 px-4 text-center">
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl md:text-5xl font-bold mb-4"
        >
          Screen-Free Coding and Computational Thinking
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-xl max-w-2xl mx-auto opacity-90"
        >
          Course 1: Build logic and creativity through hands-on, screen-free learning.
        </motion.p>
      </div>

      <section className="py-16 px-4 max-w-7xl mx-auto">
        <div className="mb-12 text-center">
          <p className="text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Screen-free coding introduces children to the logic and creativity behind programming — without using
            computers, tablets, or phones. Through hands-on activities like puzzles, games, and storytelling, kids learn
            to think like programmers: breaking problems into steps, finding patterns, and building solutions.
          </p>
          <p className="text-gray-600 max-w-3xl mx-auto leading-relaxed mt-4">
            The curriculum is divided into four quarters that build computational thinking skills step by step.
          </p>
          <p className="text-gray-600 max-w-3xl mx-auto leading-relaxed mt-4">
            It’s all about developing computational thinking — a powerful skill that helps kids break down problems into
            simple steps that a computer or a person can understand and follow.
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-gray-600 mt-8">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              <span>Quarterly enrolments (3 months)</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-primary" />
              <span>Age: 5-10 years</span>
            </div>
          </div>
        </div>

        <div className="space-y-16">
          {course1Quarters.map((quarter, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className={`flex flex-col ${
                index % 2 === 1 ? "lg:flex-row-reverse" : "lg:flex-row"
              } gap-8 items-center bg-white rounded-2xl shadow-sm overflow-hidden`}
            >
              <div className="w-full lg:w-1/2 h-64 lg:h-auto min-h-[300px]">
                <img src={quarter.image} alt={quarter.title} className="w-full h-full object-cover" />
              </div>
              <div className="w-full lg:w-1/2 p-8 lg:p-12">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">{quarter.title}</h3>
                <ul className="space-y-4">
                  {quarter.description.map((desc, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="mt-1.5 w-2 h-2 rounded-full bg-primary shrink-0" />
                      <p className="text-gray-600 leading-relaxed">{desc}</p>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
};
